import React, { useCallback, useMemo } from "react"
import { ClassProps } from "../../utility/common/props"
import styles from "./ProjectHeader.module.scss"
import HugeLogo from "../HugeLogo/HugeLogo"
import ProjectSelector, { ProjectSelectorLoading } from "../ProjectSelector/ProjectSelector"
import { definedMenuItems } from "../../utility/menu/definedMenuItems"
import MenuItemUser from "../MenuItemUser/MenuItemUser"
import {
    selectCurrentProject,
    selectFrameLoadingState,
    selectOtherProjects,
    selectThemeInitializingStatus
} from "../../store/projects/selectors"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectCurrentMainMenu } from "../../store/menu/selectors"
import { selectCurrentPermissions, selectCurrentUser } from "../../store/users/selectors"
import { useTranslation } from "react-i18next"
import OperatorStatusSelector, { OperatorStatusSelectorLoading } from "../OperatorStatusSelector/OperatorStatusSelector"
import { selectOperatorStatuses, selectOperatorStatusState } from "../../store/userOperator/selectors"
import MenuItemComponent from "../MenuItem/MenuItem"
import { getAllowedMenuItems } from "../../utility/project/getAllowedMenuItems"
import { updateOperatorStatus } from "../../store/userOperator/thunks"
import { OperatorStatusValue } from "../../models/operatorStatus"
import AsyncWithoutError from "../Async/AsyncWithoutError"
import usePermissionsCheck from "../../hooks/usePermissionsCheck"
import { ViewNewOperatorUI } from "../../permissions"
import { Newsline } from "../Newsline/Newsline"
import { useRouteMatch } from "react-router-dom"
import TelephonyFrame from "../TelephonyFrame/TelephonyFrame"
import OperatorStatusTimer from "../OperatorStatusTimer/OperatorStatusTimer"

const tNamespace = "project:"
const knowledgeBaseRoute = "/project/:projectId/knowledge-base"

interface Props extends ClassProps {
    selectProjectUrl: (projectId: string, selectedMainMenu?: string) => void
    getSelectedMenu: (menuId: string, selectedMainMenu?: string) => boolean
    selectMenu: (menuItemId: string) => void
}

const ProjectHeader: React.FC<Props> = props => {
    const { selectProjectUrl, getSelectedMenu, selectMenu } = props
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const frameState = useAppSelector(selectFrameLoadingState)
    const selectedProject = useAppSelector(selectCurrentProject)
    const selectedMainMenu = useAppSelector(selectCurrentMainMenu)
    const nonSelectedProjects = useAppSelector(selectOtherProjects)
    const operatorStatuses = useAppSelector(selectOperatorStatuses)
    const user = useAppSelector(selectCurrentUser)
    const currentPermissions = useAppSelector(selectCurrentPermissions)
    const themeInitStatus = useAppSelector(selectThemeInitializingStatus)
    const viewNewOperatorUIAllowed = usePermissionsCheck([ViewNewOperatorUI])
    const operatorStatusState = useAppSelector(selectOperatorStatusState)

    const isOperatorOffline = operatorStatusState.data?.Value === OperatorStatusValue.Offline
    const isKnowledgeBase = Boolean(useRouteMatch(knowledgeBaseRoute))
    const offlineStatus = operatorStatuses.find(status => status.Value === OperatorStatusValue.Offline)

    const handleProjectSelection = (projectId: string) => {
        offlineStatus && dispatch(updateOperatorStatus(offlineStatus))
        selectProjectUrl(projectId, selectedMainMenu)
    }

    if (!user || themeInitStatus.inProcess) return null

    return (
        <div className={styles.header}>
            <div className={styles.header__side}>
                {selectedProject && nonSelectedProjects && !frameState.inProcess ? (
                    <ProjectSelector
                        selectedProject={selectedProject}
                        nonSelectedProject={nonSelectedProjects}
                        selectProject={handleProjectSelection}
                        inHeader
                    />
                ) : (
                    <ProjectSelectorLoading t={t} inHeader />
                )}
                <TelephonyFrame />
            </div>
            <div className={styles.header__middle}>
                <HugeLogo />
            </div>
            {isKnowledgeBase && <Newsline className={styles.header__newsline} />}
            <div className={styles.header__side}>
                {definedMenuItems.Profile && (
                    <MenuItemUser
                        user={user}
                        selectMenu={selectMenu}
                        getSelectedMenu={menuId => getSelectedMenu(menuId, selectedMainMenu)}
                        className={styles.projectViewHeader__userProfile}
                        inHeader
                    />
                )}
                <AsyncWithoutError
                    dataState={operatorStatusState}
                    processView={<OperatorStatusSelectorLoading t={t} inHeader />}
                >
                    {({ data }) => (
                        <div className={styles.header__status}>
                            <OperatorStatusSelector
                                currentStatus={data}
                                statuses={operatorStatuses}
                                onChange={status => dispatch(updateOperatorStatus(status))}
                                statusTimer={<OperatorStatusTimer status={data} />}
                                inHeader
                            />
                        </div>
                    )}
                </AsyncWithoutError>
            </div>
        </div>
    )
}

export default ProjectHeader
